import { useEffect, useRef, useMemo, useCallback } from "react";
import { computeStandings, getBestThirdPlaces, areAllGroupsComplete } from "../../functionsSimulator";
import CardGroup from "./cardGroup";
import "./standingsTableComplete.sass";

export default function StandingsTableComplete({ TABS, groupsState, activeGroup, onSelect }) {
  const containerRef = useRef(null);
  const groupRefs = useRef({});
  const isAdjustingRef = useRef(false);

  const bestThirdTeams = useMemo(() => {
    if (!areAllGroupsComplete(groupsState, TABS)) return new Set();
    const result = getBestThirdPlaces(groupsState, TABS);
    if (!result?.rankedThirds) return new Set();
    return new Set(result.rankedThirds.map(t => t.team));
  }, [groupsState, TABS]);

  // 3 copias: [clon-prev, original, clon-next]
  const infiniteGroups = useMemo(() => [...TABS, ...TABS, ...TABS], [TABS]);

  // Reposicionar al bloque del medio cuando el scroll entra en el primer o tercer bloque
  const handleScroll = useCallback(() => {
    if (isAdjustingRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    const isDesktop = window.innerWidth > 780;

    if (isDesktop) {
      const { scrollTop, scrollHeight } = container;
      const blockSize = scrollHeight / 3;

      if (scrollTop < blockSize) {
        // Estamos en el primer bloque → saltar al equivalente en el medio
        isAdjustingRef.current = true;
        container.scrollTop = scrollTop + blockSize;
        isAdjustingRef.current = false;
      } else if (scrollTop >= blockSize * 2) {
        // Estamos en el tercer bloque → saltar al equivalente en el medio
        isAdjustingRef.current = true;
        container.scrollTop = scrollTop - blockSize;
        isAdjustingRef.current = false;
      }
    } else {
      const { scrollLeft, scrollWidth } = container;
      const blockSize = scrollWidth / 3;

      if (scrollLeft < blockSize) {
        isAdjustingRef.current = true;
        container.scrollLeft = scrollLeft + blockSize;
        isAdjustingRef.current = false;
      } else if (scrollLeft >= blockSize * 2) {
        isAdjustingRef.current = true;
        container.scrollLeft = scrollLeft - blockSize;
        isAdjustingRef.current = false;
      }
    }
  }, []);

  // Posicionar inicialmente en el bloque del medio (sin animación)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Desactivar smooth scroll temporalmente para el posicionamiento inicial
    container.style.scrollBehavior = "auto";

    requestAnimationFrame(() => {
      const isDesktop = window.innerWidth > 780;
      if (isDesktop) {
        container.scrollTop = container.scrollHeight / 3;
      } else {
        container.scrollLeft = container.scrollWidth / 3;
      }
      // Restaurar smooth scroll después
      requestAnimationFrame(() => {
        container.style.scrollBehavior = "";
      });
    });
  }, [TABS]);

  // Listener de scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Scroll al elemento activo cuando cambia
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeElement = groupRefs.current[`${activeGroup}-1`];
    if (!activeElement) return;

    const isDesktop = window.innerWidth > 780;

    // Desactivar el reposicionamiento durante el smooth scroll
    isAdjustingRef.current = true;

    requestAnimationFrame(() => {
      if (isDesktop) {
        container.scrollTo({
          top: activeElement.offsetTop - 87,
          behavior: "smooth",
        });
      } else {
        container.scrollTo({
          left: activeElement.offsetLeft - 20,
          behavior: "smooth",
        });
      }
      setTimeout(() => { isAdjustingRef.current = false; }, 500);
    });
  }, [activeGroup]);

  const handleGroupClick = (groupId) => {
    if (onSelect) onSelect(groupId);
  };

  const isGroupSimulated = (group) => {
    return group?.simulationState?.isSimulated ?? false;
  };

  return (
    <div className="StandingsTableComplete" ref={containerRef}>
      {infiniteGroups.map((groupId, index) => {
        const group = groupsState[groupId];
        const standings = computeStandings(group, groupId);
        const isSimulated = isGroupSimulated(group);
        const blockIndex = Math.floor(index / TABS.length);
        const isActive = groupId === activeGroup && !isSimulated;
        const uniqueKey = `${groupId}-${blockIndex}`;

        return (
          <div
            key={uniqueKey}
            ref={(el) => (groupRefs.current[uniqueKey] = el)}
            className={`StandingsTableGroup ${isActive ? "is-active" : ""} ${isSimulated ? "is-simulate" : ""}`}
            onClick={() => handleGroupClick(groupId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleGroupClick(groupId);
              }
            }}
          >
            <div className="StandingsHeader">
              <span className="GroupLabel">GRUPO {groupId}</span>
              <div className="StandingsColumns">
                <span>J</span>
                <span>G</span>
                <span>E</span>
                <span>P</span>
                <span>DIFF</span>
                <span>PTS</span>
              </div>
            </div>

            <div className="StandingsBody">
              {standings.map((row, index) => (
                <CardGroup key={row.team} row={row} index={index} isBestThird={index === 2 && bestThirdTeams.has(row.team)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}